import React, { useEffect } from "react";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import {
  addDaysIso,
  canPayWithPrize,
  canPayWithWallet,
  formatDate,
  formatWallet,
  money,
  moneyPair,
  profitOf,
  rafflePrizeLive,
  walletNeedOf,
} from "./helpers";
import { HintTip, tNegocio } from "./shared";
import { DualMoneyField, DURATION_PRESETS } from "./AccountFields";
import { useStyles } from "./styles";

export const usableCodeFilters = (product) =>
  (product?.codeFilters || []).filter(
    (filter) => filter.active !== false && String(filter.subject || "").trim()
  );

export function CartItemFields({
  startDate,
  days,
  currency,
  price,
  priceFx,
  cost,
  notes,
  codesEnabled,
  codeSubjects = [],
  codeFilters = [],
  paid,
  onChange,
  showPaid = true,
  showCodes = false,
  showSubjectPicker = true,
  payWithWallet = false,
  payWithPrize = false,
  allowWallet = true,
  customer = null,
  hideDuration = false,
}) {
  const classes = useStyles();
  const pair = moneyPair(price, priceFx);
  const localPrice = pair.local;
  const profit = profitOf(localPrice, cost);
  const prizeDays = payWithPrize ? 30 : days;
  const endDate = startDate ? addDaysIso(startDate, prizeDays) : "";
  const walletNeed = walletNeedOf({ customer, currency, price: localPrice, priceFx });
  const showPayWithWallet = allowWallet && !payWithPrize && canPayWithWallet({ customer, need: walletNeed });
  const prize = rafflePrizeLive(customer);
  const showPayWithPrize = canPayWithPrize(customer);

  useEffect(() => {
    if (payWithWallet && !showPayWithWallet) onChange({ payWithWallet: false });
  }, [payWithWallet, showPayWithWallet]);

  return (
    <>
      {hideDuration || payWithPrize ? (
        payWithPrize ? (
          <div className={classes.captionBlock} style={{ margin: "4px 0 10px" }}>
            {tNegocio("pages.services.cart.prizeDaysHint")}: {formatDate(endDate)}
          </div>
        ) : null
      ) : (
        <>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="date"
            label={tNegocio("pages.services.cart.contractDate")}
            InputLabelProps={{ shrink: true }}
            value={startDate || ""}
            onChange={(e) => onChange({ startDate: e.target.value })}
          />
          <div className={classes.filters} style={{ marginBottom: 8 }}>
            {DURATION_PRESETS.map((preset) => (
              <Button
                key={preset}
                size="small"
                color={Number(days) === preset ? "primary" : "default"}
                variant={Number(days) === preset ? "contained" : "outlined"}
                onClick={() => onChange({ days: preset })}
              >
                {tNegocio(`pages.accounts.durations.${preset}`)}
              </Button>
            ))}
          </div>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="number"
            label={tNegocio("fields.days")}
            value={days}
            onChange={(e) => onChange({ days: e.target.value })}
          />
          <div className={classes.captionBlock} style={{ margin: "4px 0 10px" }}>
            {tNegocio("cols.endDate")}: {formatDate(endDate)}
          </div>
        </>
      )}
      <DualMoneyField
        label={tNegocio("pages.accounts.salePrice")}
        hint={tNegocio("pages.accounts.salePriceHint")}
        localValue={price}
        fxValue={priceFx}
        currency={currency || "local"}
        onChange={({ local, fx, currency: nextCurrency }) =>
          onChange({ price: local, priceFx: fx, currency: nextCurrency })
        }
      />
      <div className={classes.infoField} style={{ marginTop: 4 }}>
        <div className={classes.captionBlock}>{tNegocio("pages.services.cart.profit")}</div>
        <div style={{ fontWeight: 700 }}>{money(profit)}</div>
      </div>
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        multiline
        rows={2}
        label={tNegocio("pages.services.cart.details")}
        value={notes || ""}
        onChange={(e) => onChange({ notes: e.target.value })}
      />
      {showCodes ? (
        <>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={!!codesEnabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  const available = usableCodeFilters({ codeFilters });
                  onChange({
                    codesEnabled: on,
                    codeSubjects: on ? available.map((filter) => String(filter.subject).trim()) : [],
                  });
                }}
              />
            }
            label={tNegocio("pages.services.cart.itemCodes")}
          />
          {codesEnabled && showSubjectPicker ? (
            <div
              style={{
                margin: "0 0 12px",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(15,23,42,0.10)",
                background: "rgba(37,99,235,0.04)",
              }}
            >
              <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 4 }}>
                {tNegocio("pages.codes.serviceSubjects")}
              </div>
              <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
                {tNegocio("pages.codes.serviceSubjectsHint")}
              </div>
              {!usableCodeFilters({ codeFilters }).length ? (
                <div className={classes.captionBlock}>{tNegocio("pages.codes.noProductSubjects")}</div>
              ) : (
                usableCodeFilters({ codeFilters }).map((filter) => {
                    const subject = String(filter.subject).trim();
                    const selected = (codeSubjects || []).some(
                      (item) => String(item).trim().toLowerCase() === subject.toLowerCase()
                    );
                    return (
                      <FormControlLabel
                        key={subject}
                        style={{ display: "flex", marginLeft: -4 }}
                        control={
                          <Checkbox
                            color="primary"
                            checked={selected}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...(codeSubjects || []).filter((item) => String(item).trim().toLowerCase() !== subject.toLowerCase()), subject]
                                : (codeSubjects || []).filter((item) => String(item).trim().toLowerCase() !== subject.toLowerCase());
                              onChange({ codeSubjects: next });
                            }}
                          />
                        }
                        label={subject}
                      />
                    );
                  })
              )}
            </div>
          ) : null}
        </>
      ) : null}
      {showPayWithPrize ? (
        <>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={!!payWithPrize}
                onChange={(e) =>
                  onChange({
                    payWithPrize: e.target.checked,
                    payWithWallet: e.target.checked ? false : payWithWallet,
                    paid: e.target.checked ? true : paid,
                    days: e.target.checked ? 30 : days,
                  })
                }
              />
            }
            label={tNegocio("pages.services.cart.payWithPrize")}
          />
          <div className={classes.captionBlock} style={{ margin: "-4px 0 10px" }}>
            {tNegocio("pages.services.cart.payWithPrizeHint").replace("{date}", formatDate(prize?.expiresAt))}
          </div>
        </>
      ) : null}
      {showPayWithWallet ? (
        <>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={!!payWithWallet}
                onChange={(e) => {
                  onChange({
                    payWithWallet: e.target.checked,
                    paid: e.target.checked ? true : paid,
                  });
                }}
              />
            }
            label={
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {tNegocio("pages.services.cart.payWithWallet")} ({formatWallet(customer?.walletBalance, customer?.walletCurrency)})
                <HintTip text={tNegocio("pages.services.cart.payWithWalletHint")} />
              </span>
            }
          />
        </>
      ) : null}
      {showPaid && !payWithWallet && !payWithPrize ? (
        <FormControlLabel
          control={
            <Switch color="primary" checked={!!paid} onChange={(e) => onChange({ paid: e.target.checked, payWithWallet: false, payWithPrize: false })} />
          }
          label={paid ? tNegocio("pages.services.cart.paid") : tNegocio("pages.services.cart.unpaid")}
        />
      ) : payWithWallet || payWithPrize ? (
        <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
          {payWithPrize ? tNegocio("pages.services.cart.prizePaid") : tNegocio("pages.services.cart.walletPaid")}
        </div>
      ) : null}
    </>
  );
}
